"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.queryLeverageConfigs = exports.useLeverageConfigRow = exports.useLeverageConfig = exports.useAllLeverageConfigureMap = exports.queryLeverageConfigureWithCache = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const FutureAPI_1 = require("../../../../apis/FutureAPI");
const errors_1 = require("../../../../interface/errors");
const lodash_1 = require("lodash");
const leverageMapSub = new rxjs_1.BehaviorSubject(new Map());
const loadingMap = new Map();
const TimeOut = 60 * 60 * 1000; // 毫秒级一小时
const cacheTimeMap = new Map();
const processConfigureData = (resp) => {
    return (resp || []).map((e) => {
        const newConfig = Object.assign({}, e);
        // 针对获取到的杠杆阶梯进行排序，并且设置最后一个为最大阶梯
        const sortedRows = (e.rows || []).sort((a, b) => Number(a.max_leverage) - Number(b.max_leverage));
        newConfig.rows = sortedRows;
        return newConfig;
    });
};
const queryLeverageConfigureWithCache = (symbol) => {
    if (!symbol) {
        return (0, rxjs_1.throwError)({ code: "-1", message: "no symbol filled" });
    }
    if (!symbol.includes("PERP")) {
        return (0, rxjs_1.throwError)({ code: "-1", message: "Symbol not supported: " + symbol });
    }
    const hasData = !!leverageMapSub.value.get(symbol);
    const now = Date.now();
    // 不超时返回上一次的数据
    if (now - (cacheTimeMap.get(symbol) || 0) < TimeOut && hasData) {
        return leverageMapSub.pipe((0, operators_1.map)((res) => (0, lodash_1.cloneDeep)(res.get(symbol))));
    }
    // 如果在loading中就等待数据返回
    if (loadingMap.get(symbol)) {
        return leverageMapSub.pipe((0, operators_1.filter)((res) => {
            const value = res.get(symbol);
            // 有值的时候往下走
            return !!value;
        }), (0, operators_1.map)((res) => {
            return (0, lodash_1.cloneDeep)(res.get(symbol));
        }));
    }
    loadingMap.set(symbol, true);
    return new rxjs_1.Observable((newSub) => {
        const subscribe = (0, trade_utils_lib_1.requestWithRetry)(() => (0, FutureAPI_1.queryLeverageConfigure)(symbol), {
            name: "queryLeverageConfigure-" + symbol,
        }).subscribe({
            next: (resp) => {
                loadingMap.set(symbol, false);
                const res = processConfigureData(resp);
                if (res && res.length && res[0].symbol === symbol) {
                    const value = res[0];
                    const map = leverageMapSub.value;
                    map.set(symbol, value);
                    cacheTimeMap.set(symbol, now);
                    leverageMapSub.next(map);
                    newSub.next((0, lodash_1.cloneDeep)(value));
                }
                else {
                    newSub.error(errors_1.TRADE_NOT_HAVE_LEVERAGED_CONFIG);
                }
            },
            error: (err) => {
                loadingMap.set(symbol, false);
                newSub.error(err);
            },
            complete: () => {
                loadingMap.set(symbol, false);
            },
        });
        // 需要再反注册时重置loading
        newSub.add(() => {
            loadingMap.set(symbol, false);
            subscribe.unsubscribe();
        });
    });
};
exports.queryLeverageConfigureWithCache = queryLeverageConfigureWithCache;
const SYMBOL_ALL = "ALL";
const queryALLLeverageConfigureWithCache = () => {
    const now = Date.now();
    // 是否超时
    if (now - (cacheTimeMap.get(SYMBOL_ALL) || 0) < TimeOut) {
        return leverageMapSub.pipe((0, operators_1.map)((res) => (0, lodash_1.cloneDeep)(res)));
    }
    // 是否在loading
    if (loadingMap.get(SYMBOL_ALL)) {
        return leverageMapSub.pipe((0, operators_1.map)((res) => (0, lodash_1.cloneDeep)(res)));
    }
    loadingMap.set(SYMBOL_ALL, true);
    return new rxjs_1.Observable((newSub) => {
        const subscribe = (0, trade_utils_lib_1.requestWithRetry)(() => (0, FutureAPI_1.queryLeverageConfigure)(), {
            maxRetryCount: 2,
            name: "queryALLLeverageConfigureWithCache",
        }).subscribe({
            next: (resp) => {
                loadingMap.set(SYMBOL_ALL, false);
                const res = processConfigureData(resp);
                const map = leverageMapSub.value;
                res.forEach((e) => {
                    map.set(e.symbol, e);
                    cacheTimeMap.set(e.symbol, now);
                });
                leverageMapSub.next(map);
                cacheTimeMap.set(SYMBOL_ALL, now);
                newSub.next((0, lodash_1.cloneDeep)(map));
            },
            error: (err) => {
                loadingMap.set(SYMBOL_ALL, false);
                newSub.error(err);
            },
            complete: () => {
                loadingMap.set(SYMBOL_ALL, false);
            },
        });
        // 需要再反注册时重置loading
        newSub.add(() => {
            loadingMap.set(SYMBOL_ALL, false);
            subscribe.unsubscribe();
        });
    });
};
const useAllLeverageConfigureMap = (enable = true) => {
    const [configMap, setConfigMap] = (0, react_1.useState)(new Map());
    const [error, setError] = (0, react_1.useState)();
    (0, react_1.useEffect)(() => {
        if (!enable) {
            return;
        }
        setError(undefined);
        const sub = queryALLLeverageConfigureWithCache().subscribe({
            next: (res) => {
                setConfigMap(res);
                setError(undefined);
            },
            error: (err) => {
                setError(err);
            },
        });
        return () => {
            sub.unsubscribe();
        };
    }, [enable]);
    return { configMap: configMap, leverageError: error };
};
exports.useAllLeverageConfigureMap = useAllLeverageConfigureMap;
/** 没有 config 也没有 error 的时候 是loading */
const useLeverageConfig = (symbol) => {
    const [result, setResult] = (0, react_1.useState)({});
    (0, react_1.useEffect)(() => {
        setResult({});
        if (!symbol) {
            return;
        }
        const sub = (0, exports.queryLeverageConfigureWithCache)(symbol).subscribe({
            next: (res) => {
                setResult({ leverageConfig: res });
            },
            error: (err) => {
                setResult({ leverageError: err });
            },
        });
        return () => {
            sub.unsubscribe();
        };
    }, [symbol]);
    return result;
};
exports.useLeverageConfig = useLeverageConfig;
const DefaultMaxLeverage = 10;
/**
 * 获取合约杠杆倍数相关配置
 * @param symbol 当前交易对
 * @param leverage 当前杠杆倍数
 * @returns maxLeverage: 最大杠杆倍数（设置过 defaultValue，一直都有值）
 * @returns realMaxLeverage: 没有设置 defaultValue 的 maxLeverage（maxLeverage 获取是异步的，在没有取到的时候，realMaxLeverage 是 undefined）
 * @returns curLeverageRow: 当前杠杆倍数所处配置
 * @returns leverageConfig: 该交易对总配置
 * @returns leverageError: 错误
 */
const useLeverageConfigRow = (symbol, leverage) => {
    var _a;
    const { leverageConfig, leverageError } = (0, exports.useLeverageConfig)(symbol);
    const result = (0, react_1.useMemo)(() => {
        if (!leverageConfig || !leverageConfig.rows || !leverageConfig.rows.length) {
            return;
        }
        // 以最大杠杆倍数升序 exam: 1,2,3
        const sortRows = leverageConfig.rows;
        const maxLeverage = Number(sortRows[sortRows.length - 1].max_leverage);
        let curRow = undefined;
        if (Number(leverage)) {
            curRow = sortRows.find((e) => {
                return Number(leverage) <= Number(e.max_leverage);
            });
        }
        return { maxLeverage: maxLeverage, curLeverageRow: curRow };
    }, [leverage, leverageConfig]);
    const maxLeverageWithDefault = (_a = result === null || result === void 0 ? void 0 : result.maxLeverage) !== null && _a !== void 0 ? _a : DefaultMaxLeverage;
    return {
        maxLeverage: maxLeverageWithDefault,
        realMaxLeverage: result === null || result === void 0 ? void 0 : result.maxLeverage,
        curLeverageRow: result === null || result === void 0 ? void 0 : result.curLeverageRow,
        leverageConfig: leverageConfig,
        leverageError: leverageError,
    };
};
exports.useLeverageConfigRow = useLeverageConfigRow;
/** 请求多个币对的合约配置信息 */
const queryLeverageConfigs = (symbols) => {
    const obs = symbols.map((e) => (0, exports.queryLeverageConfigureWithCache)(e));
    return (0, rxjs_1.zip)(...obs);
};
exports.queryLeverageConfigs = queryLeverageConfigs;
